import { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { CloseOutlined } from '@ant-design/icons'
import { Modal, Button } from 'antd'
import { colours } from '@qflow/theme'
import { saveAs } from 'file-saver'

import { Queries } from 'admin/lib/Api'
import { Delivery, Waste } from 'admin/lib/Records/fields_v2'

import { DATA, RAW, ENRICHED } from 'admin/pages/RecordEdit_v2/common/constants'
import { FieldDetails } from './FieldDetails'
import { useApi } from 'admin/lib/Api'

interface ChangeData {
  Action: string
  ChangeDtm: string
  ChangeType: string
  ChangedBy?: string
  NewValue: string
  OldValue: string
}

interface FieldHistory {
  name: string
  label: string
  changes: ChangeData[]
}

interface OrderedObject {
  A1: FieldHistory
  A2: FieldHistory
  Data: any
  Raw: any
  Enriched: any
}

export const RecordHistory: React.FC<{
  changes: any[]
  moduleName: string
  isImperial: boolean
  projectLocale: string
  projectTimeZone: string
  lineItemCount: any
  onCloseClick: () => void
  showHistory: boolean
  accountId: string
  recordId: string
  projectId: string
}> = ({
  changes,
  moduleName,
  isImperial = false,
  projectLocale = 'en-GB',
  projectTimeZone = 'Europe/London',
  lineItemCount = 0,
  onCloseClick,
  showHistory,
  accountId,
  recordId,
  projectId
}) => {
  const [dataView, setDataView] = useState(ENRICHED)
  const [isHistoryDownloading, setIsHistoryDownloading] = useState(false)
  const fields = moduleName === 'Waste' ? Waste : Delivery

  const LINE_ITEMS = moduleName === 'Waste' ? 'Items' : 'Products'
  const LINE_ITEM_SINGULAR = moduleName === 'Waste' ? 'Item' : 'Product'
  const companies = Queries.useCompanies()
  const api = useApi()

  const formatToTimeZone = useCallback(
    value => {
      return new Date(value).toLocaleString(projectLocale, {
        timeZone: projectTimeZone,
        timeZoneName: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: 'numeric',
        minute: 'numeric'
      })
    },
    [projectLocale, projectTimeZone]
  )

  const createChangeData = useCallback(
    (change, fieldChange, skipDataFormatting = false): ChangeData => {
      const changeData = {
        Action: change.Action,
        ChangeDtm: change.ChangeDtm,
        ChangeType: fieldChange.value[0].ChangeType,
        ChangedBy: change.ChangedBy,
        NewValue: fieldChange.value[0].NewValue,
        OldValue: fieldChange.value[0].OldValue
      }

      // If Date or Time field and not a RAW value
      if (
        !skipDataFormatting &&
        /Date$|Time$/.test(fieldChange.key.Identifier.Name)
      ) {
        changeData.NewValue = changeData.NewValue
          ? formatToTimeZone(changeData.NewValue)
          : undefined
        changeData.OldValue = changeData.OldValue
          ? formatToTimeZone(changeData.OldValue)
          : undefined
      }

      // If Delivery 'SupplierId' swap GUID with Company Name
      if (fieldChange.key.Identifier.Name === 'SupplierId' && companies.data) {
        const newValueFilteredArray = companies.data.filter(
          company => company.companyId === changeData.NewValue
        )
        const oldValueFilteredArray = companies.data.filter(
          company => company.companyId === changeData.OldValue
        )

        if (newValueFilteredArray[0]) {
          changeData.NewValue = newValueFilteredArray[0].name
        }
        if (oldValueFilteredArray[0]) {
          changeData.OldValue = oldValueFilteredArray[0].name
        }
      }

      return changeData
    },
    [companies.data, formatToTimeZone]
  )

  // First we create an ordered object to template out all fields in the expected order
  const orderedObject: OrderedObject = useMemo(() => {
    const newObject: OrderedObject = {
      A1: { name: 'Status', label: 'Status', changes: [] },
      A2: { name: 'Assignee', label: 'Assignee', changes: [] },
      Data: {},
      Raw: {},
      Enriched: {}
    }

    // Enriched and Data
    Object.entries(fields.enriched).forEach(([key]) => {
      if (fields.enriched[key].order) {
        newObject.Data[fields.enriched[key].order] = {
          name: key.charAt(0).toUpperCase() + key.slice(1),
          label: fields.enriched[key].label,
          changes: []
        }
        newObject.Enriched[fields.enriched[key].order] = {
          name: key.charAt(0).toUpperCase() + key.slice(1),
          label: fields.enriched[key].label,
          changes: []
        }
      }
    })

    // Raw
    Object.entries(fields.raw).forEach(([key]) => {
      if (fields.raw[key].order) {
        newObject.Raw[fields.raw[key].order] = {
          name: key.charAt(0).toUpperCase() + key.slice(1),
          label: fields.raw[key].label,
          changes: []
        }
      }
    })

    newObject.Data[LINE_ITEMS] = []
    newObject.Raw[LINE_ITEMS] = []
    newObject.Enriched[LINE_ITEMS] = []

    for (let i = 0; i < lineItemCount; i++) {
      // Enriched & Data
      const lineItem = {}
      const enrichedLineItem = {}
      Object.entries(fields.lineItems).forEach(([key]) => {
        if (fields.lineItems[key].order) {
          lineItem[fields.lineItems[key].order] = {
            name: key.charAt(0).toUpperCase() + key.slice(1),
            label: fields.lineItems[key].label,
            changes: []
          }
          enrichedLineItem[fields.lineItems[key].order] = {
            name: key.charAt(0).toUpperCase() + key.slice(1),
            label: fields.lineItems[key].label,
            changes: []
          }
        }
      })
      newObject.Data[LINE_ITEMS].push(lineItem)
      newObject.Enriched[LINE_ITEMS].push(enrichedLineItem)

      // Raw
      const rawLineItem = {}
      Object.entries(fields.rawLineItems).forEach(([key]) => {
        if (fields.rawLineItems[key].order) {
          rawLineItem[fields.rawLineItems[key].order] = {
            name: key.charAt(0).toUpperCase() + key.slice(1),
            label: fields.rawLineItems[key].label,
            changes: []
          }
        }
      })
      newObject.Raw[LINE_ITEMS].push(rawLineItem)
    }

    // Now, we populate the ordered object with the History
    changes.forEach(change => {
      change.FieldChanges.forEach(fieldChange => {
        // work out where the change should be added
        if (fieldChange.key && fieldChange.key.Path) {
          if (
            (fieldChange.key.Path[0] &&
              fieldChange.key.Path[0].Name === 'Status') ||
            fieldChange.key.Identifier.Name === 'Assignee'
          ) {
            // Set key to 'A1' for Status, or 'A2' for Assignee
            const key = fieldChange.key.Path[0]?.Name === 'Status' ? 'A1' : 'A2'

            newObject[key].changes.push(createChangeData(change, fieldChange))
          } else if (
            // Populate Data record data
            fieldChange.key.Path.length === 1 &&
            fieldChange.key.Path[0].Name === 'Data'
          ) {
            const name =
              fieldChange.key.Identifier.Name.charAt(0).toLowerCase() +
              fieldChange.key.Identifier.Name.slice(1)

            if (fields.enriched[name] && fields.enriched[name].order) {
              newObject.Data[fields.enriched[name].order].changes.push(
                createChangeData(change, fieldChange)
              )
            }
          } else if (
            // Populate Enriched record data
            fieldChange.key.Path.length === 1 &&
            fieldChange.key.Path[0].Name === 'Enriched'
          ) {
            const name =
              fieldChange.key.Identifier.Name.charAt(0).toLowerCase() +
              fieldChange.key.Identifier.Name.slice(1)

            if (fields.enriched[name] && fields.enriched[name].order) {
              newObject.Enriched[fields.enriched[name].order].changes.push(
                createChangeData(change, fieldChange)
              )
            }
          } else if (
            // Populate Raw record data
            fieldChange.key.Path.length === 1 &&
            fieldChange.key.Path[0].Name === 'Raw'
          ) {
            const name =
              fieldChange.key.Identifier.Name.charAt(0).toLowerCase() +
              fieldChange.key.Identifier.Name.slice(1)

            if (fields.raw[name] && fields.raw[name].order) {
              newObject.Raw[fields.raw[name].order].changes.push(
                createChangeData(change, fieldChange, true)
              )
            }
          } else if (
            // Populate Data Line-items field history
            fieldChange.key.Path.length === 3 &&
            fieldChange.key.Path[0].Name === 'Data' &&
            !fieldChange.key.Identifier.Name.startsWith('Measurement')
          ) {
            const name =
              fieldChange.key.Identifier.Name.charAt(0).toLowerCase() +
              fieldChange.key.Identifier.Name.slice(1)

            // Ignore Measurement fields (i.e. fields with Value and Unit) as we'll handle those later
            if (
              newObject.Data[LINE_ITEMS] &&
              newObject.Data[LINE_ITEMS][fieldChange.key.Path[2].ItemIndex] &&
              fields.lineItems[name] &&
              fields.lineItems[name].order &&
              !fields.lineItems[name].measurementField
            ) {
              newObject.Data[LINE_ITEMS][fieldChange.key.Path[2].ItemIndex][
                fields.lineItems[name].order
              ].changes.push(createChangeData(change, fieldChange))
            }
          } else if (
            // Populate Enriched Line-items field history
            fieldChange.key.Path.length === 3 &&
            fieldChange.key.Path[0].Name === 'Enriched' &&
            !fieldChange.key.Identifier.Name.startsWith('Measurement')
          ) {
            const name =
              fieldChange.key.Identifier.Name.charAt(0).toLowerCase() +
              fieldChange.key.Identifier.Name.slice(1)

            // Ignore Measurement fields (i.e. fields with Value and Unit) as we'll handle those later
            if (
              newObject.Enriched[LINE_ITEMS] &&
              newObject.Enriched[LINE_ITEMS][
                fieldChange.key.Path[2].ItemIndex
              ] &&
              fields.lineItems[name] &&
              fields.lineItems[name].order &&
              !fields.lineItems[name].measurementField
            ) {
              newObject.Enriched[LINE_ITEMS][fieldChange.key.Path[2].ItemIndex][
                fields.lineItems[name].order
              ].changes.push(createChangeData(change, fieldChange))
            }
          } else if (
            // Populate Raw Line-items field history
            fieldChange.key.Path.length === 3 &&
            fieldChange.key.Path[0].Name === 'Raw' &&
            !fieldChange.key.Identifier.Name.startsWith('Measurement')
          ) {
            const name =
              fieldChange.key.Identifier.Name.charAt(0).toLowerCase() +
              fieldChange.key.Identifier.Name.slice(1)

            // Ignore Measurement fields (i.e. fields with Value and Unit) as we'll handle those later
            if (
              newObject.Raw[LINE_ITEMS] &&
              newObject.Raw[LINE_ITEMS][fieldChange.key.Path[2].ItemIndex] &&
              fields.rawLineItems[name] &&
              fields.rawLineItems[name].order &&
              !fields.rawLineItems[name].measurementField
            ) {
              newObject.Raw[LINE_ITEMS][fieldChange.key.Path[2].ItemIndex][
                fields.rawLineItems[name].order
              ].changes.push(createChangeData(change, fieldChange, true))
            }
          } else if (
            // Populate Data Line-item Measurement values and units
            fieldChange.key.Path.length === 3 &&
            fieldChange.key.Path[0].Name === 'Data' &&
            newObject.Data[LINE_ITEMS][fieldChange.key.Path[2].ItemIndex] &&
            fieldChange.key.Identifier.Name.startsWith('Measurement')
          ) {
            // The almost matching field name is in Identifier,
            // Strip out the starting 'Measurement' from the string and convert the starting uppercase letter to lowercase
            let name = fieldChange.key.Identifier.Name.replace(
              'Measurement',
              ''
            )
            name = name.charAt(0).toLowerCase() + name.slice(1)

            if (fields.lineItems[name] && fields.lineItems[name].order) {
              newObject.Data[LINE_ITEMS][fieldChange.key.Path[2].ItemIndex][
                fields.lineItems[name].order
              ].changes.push(createChangeData(change, fieldChange))
            }
          } else if (
            // Populate Enriched Line-item Measurement values and units
            fieldChange.key.Path.length === 3 &&
            fieldChange.key.Path[0].Name === 'Enriched' &&
            newObject.Enriched[LINE_ITEMS][fieldChange.key.Path[2].ItemIndex] &&
            fieldChange.key.Identifier.Name.startsWith('Measurement')
          ) {
            // The almost matching field name is in Identifier,
            // Strip out the starting 'Measurement' from the string and convert the starting uppercase letter to lowercase
            let name = fieldChange.key.Identifier.Name.replace(
              'Measurement',
              ''
            )
            name = name.charAt(0).toLowerCase() + name.slice(1)

            if (fields.lineItems[name] && fields.lineItems[name].order) {
              newObject.Enriched[LINE_ITEMS][fieldChange.key.Path[2].ItemIndex][
                fields.lineItems[name].order
              ].changes.push(createChangeData(change, fieldChange))
            }
          } else if (
            // Populate Raw Line-item Measurement values and units
            fieldChange.key.Path.length === 3 &&
            fieldChange.key.Path[0].Name === 'Raw' &&
            newObject.Raw[LINE_ITEMS][fieldChange.key.Path[2].ItemIndex] &&
            fieldChange.key.Identifier.Name.startsWith('Measurement')
          ) {
            // The almost matching field name is in Identifier,
            // Strip out the starting 'Measurement' from the string and convert the starting uppercase letter to lowercase
            let name = fieldChange.key.Identifier.Name.replace(
              'Measurement',
              ''
            )
            name = name.charAt(0).toLowerCase() + name.slice(1)

            if (fields.rawLineItems[name] && fields.rawLineItems[name].order) {
              newObject.Raw[LINE_ITEMS][fieldChange.key.Path[2].ItemIndex][
                fields.rawLineItems[name].order
              ].changes.push(createChangeData(change, fieldChange, true))
            }
          }
        }
      })
    })

    return newObject
  }, [
    LINE_ITEMS,
    changes,
    createChangeData,
    fields.enriched,
    fields.lineItems,
    fields.raw,
    fields.rawLineItems,
    lineItemCount
  ])

  const toggleAllDetails = (event: any) => {
    const groupTitleElement = event.target.nextElementSibling
      ? event.target
      : event.target.parentElement
    const fieldGroup = groupTitleElement.nextElementSibling
    fieldGroup.querySelectorAll('details').forEach(detail => {
      detail.open = !detail.open
    })
  }

  const downloadRecordHistory = async () => {
    if (api && !isHistoryDownloading) {
      setIsHistoryDownloading(true)
      const [blob, status, error] = await api.downloadRecordHistory(
        accountId,
        projectId,
        moduleName,
        recordId
      )
      if (error) {
        Modal.error({
          title: 'Download History Failed',
          content: `Error downloading record history: ${error}`
        })
      } else if ([200].includes(status)) {
        setIsHistoryDownloading(false)
        saveAs(blob, `record-history_${recordId}.xlsx`)
      }
    }
    return
  }

  // If showHistory = False return null
  if (!showHistory) {
    return null
  }

  return (
    <HistoryWrapper className="RecordHistory">
      <TitleBar>
        <Title>Record History</Title>
        <TitleBarRight>
          <DataToggle>
            <DataToggleItem
              className={dataView === RAW ? 'active' : ''}
              onClick={() => setDataView(RAW)}
            >
              Raw
            </DataToggleItem>
            <DataToggleItem
              className={dataView === ENRICHED ? 'active' : ''}
              onClick={() => setDataView(ENRICHED)}
            >
              Enriched
            </DataToggleItem>
            <DataToggleItem
              className={dataView === DATA ? 'active' : ''}
              onClick={() => setDataView(DATA)}
            >
              Client
            </DataToggleItem>
          </DataToggle>

          <CloseOutlined
            onClick={onCloseClick}
            style={{ marginRight: '2px' }}
          />
        </TitleBarRight>
      </TitleBar>
      {dataView === DATA && (
        <HistorySubWrapper>
          <SubTitleWrapper>
            <SubTitle onClick={toggleAllDetails}>
              {moduleName.toUpperCase()}
            </SubTitle>
            <Button
              type="link"
              disabled={isHistoryDownloading}
              loading={isHistoryDownloading}
              onClick={downloadRecordHistory}
            >
              Download
            </Button>
          </SubTitleWrapper>
          <FieldsGroup>
            <FieldDetails
              key={'status'}
              label={orderedObject.A1.label}
              changes={orderedObject.A1.changes}
            />
            <FieldDetails
              key={'assignee'}
              label={orderedObject.A2.label}
              changes={orderedObject.A2.changes}
            />

            {Object.entries(orderedObject.Data).map(([key, value]) => {
              const typedValue = value as FieldHistory
              if (
                typedValue.label &&
                typedValue.changes &&
                typedValue.changes.length > 0
              ) {
                return (
                  <FieldDetails
                    key={`${key}-${typedValue.name}`}
                    label={typedValue.label}
                    changes={typedValue.changes}
                  />
                )
              } else {
                return ''
              }
            })}
          </FieldsGroup>
          {orderedObject.Data[LINE_ITEMS] &&
            orderedObject.Data[LINE_ITEMS].map((lineItem, index: number) => {
              const currentName = lineItem[1].changes.slice(-1)[0]?.NewValue
              const currentIsDeleted =
                lineItem[101].changes.slice(-1)[0]?.NewValue === 'True'
              const currentIsRemoved =
                lineItem[102].changes.slice(-1)[0]?.NewValue === 'True'

              return (
                <LineItemWrapper key={`${LINE_ITEMS}-${index}`}>
                  <LineItemTitle onClick={toggleAllDetails}>
                    {`${LINE_ITEM_SINGULAR.toUpperCase()} ${index + 1}: `}
                    {currentIsDeleted
                      ? 'DELETED '
                      : currentIsRemoved
                        ? 'REMOVED '
                        : ''}
                    <span
                      style={
                        currentIsDeleted || currentIsRemoved
                          ? { opacity: '0.667' }
                          : {}
                      }
                    >
                      {currentName ? currentName : `New ${LINE_ITEM_SINGULAR}`}
                    </span>
                  </LineItemTitle>
                  <FieldsGroup>
                    {Object.entries(lineItem).map(([key, value]) => {
                      const typedValue = value as FieldHistory
                      if (
                        typedValue.label &&
                        typedValue.changes &&
                        typedValue.changes.length > 0
                      ) {
                        return (
                          <FieldDetails
                            key={`${LINE_ITEMS}-${index}-${key}-${typedValue.name}`}
                            label={typedValue.label}
                            changes={typedValue.changes}
                          />
                        )
                      } else {
                        return ''
                      }
                    })}
                  </FieldsGroup>
                </LineItemWrapper>
              )
            })}
        </HistorySubWrapper>
      )}
      {dataView === ENRICHED && (
        <HistorySubWrapper>
          <SubTitleWrapper>
            <SubTitle onClick={toggleAllDetails}>
              {moduleName.toUpperCase()}
            </SubTitle>
            <Button
              type="link"
              disabled={isHistoryDownloading}
              loading={isHistoryDownloading}
              onClick={downloadRecordHistory}
            >
              Download
            </Button>
          </SubTitleWrapper>
          <FieldsGroup>
            <FieldDetails
              key={'status'}
              label={orderedObject.A1.label}
              changes={orderedObject.A1.changes}
            />
            <FieldDetails
              key={'assignee'}
              label={orderedObject.A2.label}
              changes={orderedObject.A2.changes}
            />

            {Object.entries(orderedObject.Enriched).map(([key, value]) => {
              const typedValue = value as FieldHistory
              if (
                typedValue.label &&
                typedValue.changes &&
                typedValue.changes.length > 0
              ) {
                return (
                  <FieldDetails
                    key={`${key}-${typedValue.name}`}
                    label={typedValue.label}
                    changes={typedValue.changes}
                  />
                )
              } else {
                return ''
              }
            })}
          </FieldsGroup>
          {orderedObject.Enriched[LINE_ITEMS] &&
            orderedObject.Enriched[LINE_ITEMS].map(
              (lineItem, index: number) => {
                const currentName = lineItem[1]
                  ? lineItem[1].changes.slice(-1)[0]?.NewValue
                  : ''
                const currentIsDeleted = lineItem[101]
                  ? lineItem[101].changes.slice(-1)[0]?.NewValue === 'True'
                  : false
                const currentIsRemoved = lineItem[102]
                  ? lineItem[102].changes.slice(-1)[0]?.NewValue === 'True'
                  : false

                return (
                  <LineItemWrapper key={`${LINE_ITEMS}-${index}`}>
                    <LineItemTitle onClick={toggleAllDetails}>
                      {`${LINE_ITEM_SINGULAR.toUpperCase()} ${index + 1}: `}
                      {currentIsDeleted
                        ? 'DELETED '
                        : currentIsRemoved
                          ? 'REMOVED '
                          : ''}
                      <span
                        style={
                          currentIsDeleted || currentIsRemoved
                            ? { opacity: '0.667' }
                            : {}
                        }
                      >
                        {currentName
                          ? currentName
                          : `New ${LINE_ITEM_SINGULAR}`}
                      </span>
                    </LineItemTitle>
                    <FieldsGroup>
                      {Object.entries(lineItem).map(([key, value]) => {
                        const typedValue = value as FieldHistory
                        if (
                          typedValue.label &&
                          typedValue.changes &&
                          typedValue.changes.length > 0
                        ) {
                          return (
                            <FieldDetails
                              key={`${LINE_ITEMS}-${index}-${key}-${typedValue.name}`}
                              label={typedValue.label}
                              changes={typedValue.changes}
                            />
                          )
                        } else {
                          return ''
                        }
                      })}
                    </FieldsGroup>
                  </LineItemWrapper>
                )
              }
            )}
        </HistorySubWrapper>
      )}
      {dataView === RAW && (
        <HistorySubWrapper>
          <SubTitleWrapper>
            <SubTitle onClick={toggleAllDetails}>
              {moduleName.toUpperCase()}
            </SubTitle>
            <Button
              type="link"
              disabled={isHistoryDownloading}
              loading={isHistoryDownloading}
              onClick={downloadRecordHistory}
            >
              Download
            </Button>
          </SubTitleWrapper>
          <FieldsGroup>
            <FieldDetails
              key={'status'}
              label={orderedObject.A1.label}
              changes={orderedObject.A1.changes}
            />
            <FieldDetails
              key={'assignee'}
              label={orderedObject.A2.label}
              changes={orderedObject.A2.changes}
            />

            {Object.entries(orderedObject.Raw).map(([key, value]) => {
              const typedValue = value as FieldHistory
              if (
                typedValue.label &&
                typedValue.changes &&
                typedValue.changes.length > 0
              ) {
                return (
                  <FieldDetails
                    key={`${key}-${typedValue.name}`}
                    label={typedValue.label}
                    changes={typedValue.changes}
                  />
                )
              } else {
                return ''
              }
            })}
          </FieldsGroup>
          {orderedObject.Raw[LINE_ITEMS] &&
            orderedObject.Raw[LINE_ITEMS].map((lineItem, index: number) => {
              const currentName = lineItem[1].changes.slice(-1)[0]?.NewValue
              const currentIsDeleted =
                lineItem[101].changes.slice(-1)[0]?.NewValue === 'True'
              const currentIsRemoved =
                lineItem[102].changes.slice(-1)[0]?.NewValue === 'True'

              return (
                <LineItemWrapper key={`${LINE_ITEMS}-${index}`}>
                  <LineItemTitle onClick={toggleAllDetails}>
                    {`${LINE_ITEM_SINGULAR.toUpperCase()} ${index + 1}: `}
                    {currentIsDeleted
                      ? 'DELETED '
                      : currentIsRemoved
                        ? 'REMOVED '
                        : ''}
                    <span
                      style={
                        currentIsDeleted || currentIsRemoved
                          ? { opacity: '0.667' }
                          : {}
                      }
                    >
                      {currentName ? currentName : `New ${LINE_ITEM_SINGULAR}`}
                    </span>
                  </LineItemTitle>
                  <FieldsGroup>
                    {Object.entries(lineItem).map(([key, value]) => {
                      const typedValue = value as FieldHistory
                      if (
                        typedValue.label &&
                        typedValue.changes &&
                        typedValue.changes.length > 0
                      ) {
                        return (
                          <FieldDetails
                            key={`${LINE_ITEMS}-${index}-${key}-${typedValue.name}`}
                            label={typedValue.label}
                            changes={typedValue.changes}
                          />
                        )
                      } else {
                        return ''
                      }
                    })}
                  </FieldsGroup>
                </LineItemWrapper>
              )
            })}
        </HistorySubWrapper>
      )}
    </HistoryWrapper>
  )
}

export const HistoryWrapper = styled.div`
  height: calc(100% - 15px);
  min-width: 350px;
  max-width: 350px;
  margin: 10px 10px 5px 0;
  padding: 12px 8px 8px 16px;
  border: 1px solid ${colours.CLOUD_GREY};
  border-radius: 5px;
`
export const TitleBar = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  border-bottom: 1px solid ${colours.CLOUD_GREY};
  margin: 0 -8px 0 -16px;
  padding: 0 8px 6px 16px;
`
export const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
`
export const TitleBarRight = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: -6px;
`
export const DataToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: solid 1px ${colours.CLOUD_GREY};
`

export const DataToggleItem = styled.div`
  line-height: 24px;
  font-weight: 500;
  padding-bottom: 4px;

  &.active,
  &:hover {
    cursor: pointer;
    color: ${colours.TEAL};
    border-bottom: solid 2px ${colours.TEAL};
    margin-bottom: -1px;
  }

  &.disabled,
  &.disabled:hover {
    cursor: default;
    color: ${colours.BORDER_GREY};
    border-bottom: none;
    margin-bottom: 0;
  }
`
export const HistorySubWrapper = styled.div`
  height: calc(100% - 32px);
  overflow-y: scroll;
`
export const SubTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  padding: 16px 0 10px;
`
export const FieldsGroup = styled.div`
  border: 1px solid ${colours.CLOUD_GREY};
  border-radius: 5px;
  margin-right: 8px;
`
export const LineItemWrapper = styled.div`
  margin-top: 24px;
`
export const LineItemTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  padding: 0 0 10px;
`
export const SubTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-right: 8px;
`
